import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Edit2, Trash2, Shield, Search, Filter, Save, X, User, RefreshCw } from 'lucide-react';
import { ROLES } from '../../contexts/AuthContext';

const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [connectionFilter, setConnectionFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    photo: '',
    roles: [],
    active: true,
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const cardClasses = 'bg-white/10 backdrop-blur-md border border-white/20';

  // Cargar usuarios del backend
  useEffect(() => {
    fetchUsers();
    
    // Actualización automática DESACTIVADA temporalmente por rendimiento
    // const interval = setInterval(fetchUsers, 120000);
    // return () => clearInterval(interval);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        setFilteredUsers(data);
      } else {
        console.error('Error al cargar usuarios');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = users;

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por rol
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Filtrar por estado (activo/inactivo)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.active : !user.active
      );
    }

    // Filtrar por estado de conexión
    if (connectionFilter !== 'all') {
      filtered = filtered.filter(user => {
        const userStatus = user.status || 'offline';
        return userStatus === connectionFilter;
      });
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, statusFilter, connectionFilter, users]);

  const handleOpenModal = (user = null) => {
    setSaveError('');
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        name: user.name,
        email: user.email,
        photo: user.photo || '',
        roles: user.roles || [],
        active: user.active,
      });
      setSelectedRoles(user.roles || []);
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        name: '',
        email: '',
        photo: '',
        roles: [],
        active: true,
      });
      setSelectedRoles([]);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleSaveUser = async () => {
    if (saving) return;
    setSaveError('');
    if (!formData.username.trim() || !formData.name.trim() || !formData.email.trim()) {
      setSaveError('Usuario, nombre y email son obligatorios');
      return;
    }
    const userData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      photo: formData.photo || null,
      roles: selectedRoles,
      active: formData.active,
    };

    setSaving(true);
    try {
      const payload = editingUser ? userData : {
        ...userData,
        username: formData.username.trim(),
        password: formData.password || 'temporal123',
      };
      const response = await fetch(`${API_URL}/users${editingUser ? `/${editingUser.id}` : ''}`, {
        method: editingUser ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const detail = data?.detail;
        const message = typeof detail === 'string' ? detail : Array.isArray(detail)
          ? detail.map(item => item.msg).join('. ')
          : `No se pudo guardar el usuario (HTTP ${response.status})`;
        throw new Error(message);
      }
      alert(editingUser ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente');
      handleCloseModal();
      fetchUsers(); // Recargar usuarios
    } catch (error) {
      setSaveError(error instanceof TypeError
        ? 'No se pudo conectar con el servidor. Verifica la conexión e intenta nuevamente.'
        : error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        const response = await fetch(`${API_URL}/users/${userId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          setUsers(users.filter(u => u.id !== userId));
          alert('Usuario eliminado correctamente');
        } else {
          alert('Error al eliminar usuario');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error al eliminar usuario');
      }
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/users/${userId}/toggle-status`, {
        method: 'POST',
      });

      if (response.ok) {
        setUsers(users.map(u => 
          u.id === userId 
            ? { ...u, active: !u.active }
            : u
        ));
      } else {
        alert('Error al cambiar estado');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al cambiar estado');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      [ROLES.ADMIN]: 'Admin',
      [ROLES.INNOVATION_GERENTE]: 'Innovation Gerente',
      [ROLES.KLOUD_OPS_GERENTE]: 'Kloud OPS Gerente',
      [ROLES.KLOUD_OPS_COORDINADOR]: 'Kloud OPS Coord',
      [ROLES.KLOUD_OPS_AGENTE]: 'Kloud OPS Agente',
      [ROLES.KLOUD_SAAS_GERENTE]: 'Kloud SaaS Gerente',
      [ROLES.KLOUD_SAAS_COORDINADOR]: 'Kloud SaaS Coord',
      [ROLES.KLOUD_SAAS_AGENTE]: 'Kloud SaaS Agente',
      [ROLES.MESA_SPIN_GERENTE]: 'Mesa Spin Gerente',
      [ROLES.MESA_SPIN_COORDINADOR]: 'Mesa Spin Coord',
      [ROLES.MESA_SPIN_AGENTE]: 'Mesa Spin Agente',
      [ROLES.FINOPS_GERENTE]: 'FINOPS Gerente',
      [ROLES.FINOPS_COORDINADOR]: 'FINOPS Coord',
      [ROLES.FINOPS_ANALISTA]: 'FINOPS Analista',
      [ROLES.FINOPS_PRESUPUESTO]: 'FINOPS Presupuestos',
      [ROLES.QA_GERENTE]: 'QA Gerente',
      [ROLES.QA_COORDINADOR]: 'QA Coord',
      [ROLES.QA_ANALISTA]: 'QA Analista',
      [ROLES.SERVICIOS_GERENTE]: 'Servicios Gerente',
      [ROLES.SERVICIOS_COORDINADOR]: 'Servicios Coord',
      [ROLES.SERVICIOS_ANALISTA]: 'Servicios Analista',
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    if (role === ROLES.ADMIN) return 'bg-purple-500/20 text-purple-300 border-purple-500/50';
    if (role.includes('gerente')) return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
    if (role.includes('coordinador')) return 'bg-green-500/20 text-green-300 border-green-500/50';
    return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-400';
      case 'away': return 'bg-yellow-400';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'online': return 'En línea';
      case 'away': return 'Ausente';
      case 'offline': return 'Desconectado';
      default: return 'Desconocido';
    }
  };

  const stats = {
    total: users.length,
    active: users.filter(u => u.active).length,
    inactive: users.filter(u => !u.active).length,
    admins: users.filter(u => u.role === ROLES.ADMIN).length,
    online: users.filter(u => u.status === 'online').length,
    away: users.filter(u => u.status === 'away').length,
    offline: users.filter(u => u.status === 'offline' || !u.status).length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
            Administración de Usuarios
          </h1>
          <p className="text-lg opacity-80 mt-2">Gestión de usuarios y roles del sistema</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className={`${cardClasses} p-6 rounded-2xl`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-70">Total Usuarios</p>
                <p className="text-3xl font-bold mt-1">{stats.total}</p>
              </div>
              <Users className="w-10 h-10 opacity-50" />
            </div>
          </div>

          <div className={`${cardClasses} p-6 rounded-2xl`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-70">Activos</p>
                <p className="text-3xl font-bold mt-1 text-green-400">{stats.active}</p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className={`${cardClasses} p-6 rounded-2xl`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-70">Inactivos</p>
                <p className="text-3xl font-bold mt-1 text-red-400">{stats.inactive}</p>
              </div>
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className={`${cardClasses} p-6 rounded-2xl`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-70">Administradores</p>
                <p className="text-3xl font-bold mt-1 text-purple-400">{stats.admins}</p>
              </div>
              <Shield className="w-10 h-10 opacity-50" />
            </div>
          </div>
        </div>

        {/* Estado de Conexión en Tiempo Real */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className={`${cardClasses} p-6 rounded-2xl border-l-4 border-green-400`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-70 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  En Línea
                </div>
                <p className="text-3xl font-bold mt-1 text-green-400">{stats.online}</p>
              </div>
              <div className="text-xs opacity-60">Activos ahora</div>
            </div>
          </div>

          <div className={`${cardClasses} p-6 rounded-2xl border-l-4 border-yellow-400`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-70 flex items-center gap-2">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                  Ausentes
                </div>
                <p className="text-3xl font-bold mt-1 text-yellow-400">{stats.away}</p>
              </div>
              <div className="text-xs opacity-60">Inactivos +5min</div>
            </div>
          </div>

          <div className={`${cardClasses} p-6 rounded-2xl border-l-4 border-gray-400`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-70 flex items-center gap-2">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  Desconectados
                </div>
                <p className="text-3xl font-bold mt-1 text-gray-400">{stats.offline}</p>
              </div>
              <div className="text-xs opacity-60">Sesión cerrada</div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className={`${cardClasses} p-6 rounded-2xl mb-8`}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2 flex-1 min-w-[250px]">
              <Search className="w-5 h-5 opacity-70" />
              <input
                type="text"
                placeholder="Buscar por nombre, usuario o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 opacity-70" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Todos los roles</option>
                <option value={ROLES.ADMIN}>Administrador</option>
                <option value={ROLES.KLOUD_OPS_GERENTE}>Gerente Kloud OPS</option>
                <option value={ROLES.MESA_SPIN_GERENTE}>Gerente Mesa Spin</option>
                <option value={ROLES.FINOPS_GERENTE}>Gerente FINOPS</option>
                <option value={ROLES.QA_GERENTE}>Gerente QA</option>
                <option value={ROLES.SERVICIOS_GERENTE}>Gerente Servicios</option>
              </select>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>

            <select
              value={connectionFilter}
              onChange={(e) => setConnectionFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Todas las conexiones</option>
              <option value="online">🟢 En línea</option>
              <option value="away">🟡 Ausentes</option>
              <option value="offline">⚪ Desconectados</option>
            </select>

            <button
              onClick={fetchUsers}
              className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-lg font-semibold hover:bg-white/20 transition-all duration-200"
              title="Actualizar lista"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              <span>Actualizar</span>
            </button>

            <button
              onClick={() => handleOpenModal()}
              className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-indigo-500 px-4 py-2 rounded-lg font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all duration-200"
            >
              <UserPlus className="w-5 h-5" />
              <span>Nuevo Usuario</span>
            </button>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm opacity-70">
            <span>Mostrando {filteredUsers.length} de {users.length} usuarios</span>
            <span className="text-yellow-400">
              ⚠️ Sistema de presencia desactivado temporalmente
            </span>
          </div>
        </div>

        {/* Users Table */}
        <div className={`${cardClasses} rounded-2xl overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Usuario</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Roles</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Conexión</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Activo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Último Login</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative flex-shrink-0">
                          {user.photo ? (
                            <img 
                              src={user.photo} 
                              alt={user.name}
                              className="w-10 h-10 rounded-full border-2 border-white/20"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{user.name}</p>
                          <p className="text-sm opacity-70">@{user.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm opacity-80">{user.email}</td>
                    <td className="px-6 py-4">
                      {user.roles && user.roles.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((role, idx) => (
                            <span key={idx} className={`px-2 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(role)}`}>
                              {getRoleLabel(role)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Sin roles</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 ${getStatusColor(user.status)} rounded-full`}></div>
                        <span className="text-xs">{getStatusLabel(user.status)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(user.id)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.active
                            ? 'bg-green-500/20 text-green-300'
                            : 'bg-red-500/20 text-red-300'
                        }`}
                      >
                        {user.active ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {user.last_login ? (
                        <div>
                          <div className="opacity-80">
                            {new Date(user.last_login).toLocaleString('es-MX', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          <div className="text-xs opacity-50 mt-1">
                            {(() => {
                              const diff = Date.now() - new Date(user.last_login).getTime();
                              const minutes = Math.floor(diff / 60000);
                              const hours = Math.floor(minutes / 60);
                              const days = Math.floor(hours / 24);
                              
                              if (minutes < 1) return 'Hace un momento';
                              if (minutes < 60) return `Hace ${minutes} min`;
                              if (hours < 24) return `Hace ${hours}h`;
                              return `Hace ${days}d`;
                            })()}
                          </div>
                        </div>
                      ) : (
                        <span className="opacity-50 italic">Nunca</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleOpenModal(user)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 opacity-70">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No se encontraron usuarios</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Crear/Editar Usuario */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" aria-label={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} className={`${cardClasses} rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button
                onClick={handleCloseModal}
                disabled={saving}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Usuario</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="usuario.nombre"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Nombre Completo</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Juan Pérez"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="usuario@empresa.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Foto de Perfil (URL)
                </label>
                <input
                  type="text"
                  value={formData.photo}
                  onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border bg-gray-800 border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="https://ejemplo.com/foto.jpg (opcional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Roles (selecciona uno o más)
                </label>
                <div className="max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg p-3 space-y-2">
                  {Object.entries(ROLES).map(([key, value]) => (
                    <label
                      key={value}
                      className="flex items-center space-x-3 p-2 hover:bg-white/5 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(value)}
                        onChange={() => handleToggleRole(value)}
                        className="w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                      />
                      <span className="text-sm">{getRoleLabel(value)}</span>
                    </label>
                  ))}
                </div>
                {selectedRoles.length === 0 && (
                  <p className="text-xs text-yellow-400 mt-2">
                    ⚠️ Usuario sin roles no podrá acceder a ninguna área
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                />
                <label htmlFor="active" className="text-sm font-medium">
                  Usuario activo
                </label>
              </div>
            </div>

            {saveError && <p role="alert" className="mt-4 text-sm text-red-300">{saveError}</p>}
            <div className="flex items-center space-x-4 mt-8">
              <button
                onClick={handleSaveUser}
                disabled={saving}
                className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-500 to-indigo-500 px-4 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all duration-200"
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Guardando...' : 'Guardar'}</span>
              </button>
              <button
                onClick={handleCloseModal}
                disabled={saving}
                className="flex-1 px-4 py-3 rounded-lg font-semibold bg-white/10 hover:bg-white/20 transition-all duration-200"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
